<template>
  <div class="hero">
    <div class="hero-aurora a1"></div>
    <div class="hero-aurora a2"></div>
    <div class="hero-aurora a3"></div>
    <span v-for="n in 14" :key="n" class="hero-leaf" :style="leafStyle(n)"><i class="fas fa-leaf"></i></span>

    <div class="hero-content">
      <div class="hero-eyebrow"><i class="fas fa-globe-europe"></i> {{ stats.scope === 'user' ? 'Your forests hold' : 'Our forests hold' }}</div>
      <div class="hero-number">
        <template v-if="co2 !== null">
          <animated-number :value="co2" :decimals="0" locale="en-GB" :duration="2400"/>
        </template>
        <span v-else class="hero-skeleton"></span>
        <span class="hero-unit">tonnes of CO2</span>
      </div>

      <div class="hero-live" v-if="perMs > 0">
        <span class="live-dot"></span>
        <span>Captured while you are on this page:</span>
        <strong>{{ liveKg }} kg</strong>
      </div>

      <div class="hero-eq">
        <div v-for="(e, idx) in equivalences" :key="e.key" class="eq" :style="{ animationDelay: (0.25 + idx * 0.12) + 's' }">
          <span class="eq-icon"><i :class="e.icon"></i></span>
          <div class="eq-text">
            <div class="eq-value"><animated-number :value="e.shown" :decimals="e.decimals" :suffix="e.suffix" locale="en-GB" :duration="2600"/></div>
            <div class="eq-label">{{ e.label }}</div>
          </div>
        </div>
      </div>
      <div class="hero-note">Approximate equivalences of the CO2 stored today: US EPA factors (cars, homes, phone charges) and ICAO data (flights).</div>
    </div>
  </div>
</template>

<script>
import AnimatedNumber from '../AnimatedNumber.vue';
import { fmt } from '../theme';

// tonnes of CO2 per unit: US EPA Greenhouse Gas Equivalencies Calculator (cars, homes, phone charges; 2022 data) and
// ICAO-based figures for the flight (Madrid-New York return, economy, CO2 only); same as the sales simulator
const FACTORS = [
  { key: 'cars', icon: 'fas fa-car-side', per: 4.29, label: 'cars off the road for a year' },
  { key: 'homes', icon: 'fas fa-home', per: 7.45, label: "homes' energy for a year" },
  { key: 'flights', icon: 'fas fa-plane-departure', per: 0.636, label: 'Madrid–New York return flights' },
  { key: 'phones', icon: 'fas fa-mobile-alt', per: 0.0000124, label: 'smartphones fully charged' },
];
const YEAR_MS = 365.25 * 24 * 3600 * 1000;

export default {
  name: 'ImpactHero',
  components: { AnimatedNumber },
  props: { stats: { type: Object, required: true } },
  data() {
    return { openedAt: Date.now(), now: Date.now(), frame: null };
  },
  computed: {
    co2() {
      const i = this.stats.insights;
      return i && i.totals ? i.totals.co2 : null;
    },
    perMs() {
      const o = this.stats.overview || {};
      return (Number(o.totalTheoreticalYearlyIncrement) || 0) / YEAR_MS;
    },
    liveKg() {
      return fmt((this.now - this.openedAt) * this.perMs * 1000, 3);
    },
    equivalences() {
      const c = this.co2 || 0;
      return FACTORS.map((f) => {
        const value = Math.round(c / f.per);
        // billions / millions abbreviated, so the figure fits its tile on a phone
        if (value >= 1e9) return { ...f, shown: value / 1e9, decimals: 1, suffix: ' B' };
        if (value >= 1e7) return { ...f, shown: value / 1e6, decimals: 1, suffix: ' M' };
        return { ...f, shown: value, decimals: 0, suffix: '' };
      });
    }
  },
  mounted() {
    let last = 0;
    const tick = (t) => {
      if (t - last > 60) { this.now = Date.now(); last = t; }
      this.frame = requestAnimationFrame(tick);
    };
    this.frame = requestAnimationFrame(tick);
  },
  beforeDestroy() {
    if (this.frame) cancelAnimationFrame(this.frame);
  },
  methods: {
    leafStyle(n) {
      // deterministic pseudo-random spread so the leaves do not jump on re-render
      const r = (k) => ((Math.sin(n * 12.9898 + k * 78.233) * 43758.5453) % 1 + 1) % 1;
      return {
        left: `${Math.round(r(1) * 100)}%`,
        animationDuration: `${9 + Math.round(r(2) * 9)}s`,
        animationDelay: `-${Math.round(r(3) * 12)}s`,
        fontSize: `${0.55 + r(4) * 0.7}rem`,
        opacity: 0.18 + r(5) * 0.3,
      };
    }
  }
};
</script>

<style scoped lang="scss">
.hero {
  position: relative;
  height: 100%;
  border-radius: 22px;
  overflow: hidden;
  color: #fff;
  background: linear-gradient(135deg, #0b3324 0%, #11573b 45%, #0c6a66 100%);
  box-shadow: 0 24px 60px -24px rgba(12, 106, 102, 0.7);
  isolation: isolate;
}

.hero-aurora {
  position: absolute;
  border-radius: 50%;
  filter: blur(40px);
  opacity: 0.55;
  z-index: -1;
  mix-blend-mode: screen;
}
.a1 { width: 60%; height: 80%; left: -10%; top: -30%; background: #3ddc97; animation: drift1 16s ease-in-out infinite alternate; }
.a2 { width: 55%; height: 70%; right: -15%; top: 10%; background: #2fd0c4; animation: drift2 19s ease-in-out infinite alternate; }
.a3 { width: 45%; height: 60%; left: 25%; bottom: -35%; background: #9ed77f; animation: drift3 22s ease-in-out infinite alternate; opacity: 0.35; }

@keyframes drift1 { to { transform: translate(18%, 22%) scale(1.15); } }
@keyframes drift2 { to { transform: translate(-22%, -12%) scale(0.9); } }
@keyframes drift3 { to { transform: translate(-15%, -20%) scale(1.2); } }

.hero-leaf {
  position: absolute;
  bottom: -20px;
  color: #c9f7b3;
  z-index: -1;
  animation-name: rise;
  animation-timing-function: linear;
  animation-iteration-count: infinite;
}

@keyframes rise {
  0% { transform: translateY(0) rotate(0deg); }
  50% { transform: translateY(-180px) translateX(18px) rotate(160deg); }
  100% { transform: translateY(-420px) translateX(-10px) rotate(340deg); opacity: 0; }
}

.hero-content {
  position: relative;
  height: 100%;
  padding: 22px 26px 16px;
  display: flex;
  flex-direction: column;
}

.hero-eyebrow {
  font-size: 0.78rem;
  font-weight: 800;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.72);
  i { margin-right: 6px; }
}

.hero-number {
  margin-top: 6px;
  font-family: 'Sora', 'Plus Jakarta Sans', sans-serif;
  font-weight: 800;
  font-size: clamp(2.2rem, 4.4vw, 3.6rem);
  line-height: 1;
  letter-spacing: -0.02em;
  text-shadow: 0 6px 30px rgba(61, 220, 151, 0.45);
  font-variant-numeric: tabular-nums;
}

.hero-unit {
  display: block;
  margin-top: 6px;
  font-size: 0.95rem;
  font-weight: 600;
  letter-spacing: 0;
  color: rgba(255, 255, 255, 0.78);
  text-shadow: none;
}

.hero-skeleton {
  display: inline-block;
  width: 220px;
  height: 0.8em;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.18);
  animation: pulse 1.2s ease-in-out infinite alternate;
}

@keyframes pulse { to { opacity: 0.4; } }

.hero-live {
  margin-top: 14px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  align-self: flex-start;
  font-size: 0.82rem;
  padding: 6px 14px;
  border-radius: 999px;
  background: rgba(0, 0, 0, 0.22);
  border: 1px solid rgba(255, 255, 255, 0.14);
  backdrop-filter: blur(6px);

  strong { font-variant-numeric: tabular-nums; color: #b9f5a1; }
}

.live-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #3ddc97;
  box-shadow: 0 0 0 0 rgba(61, 220, 151, 0.7);
  animation: ping 1.6s infinite;
}

@keyframes ping {
  0% { box-shadow: 0 0 0 0 rgba(61, 220, 151, 0.7); }
  70% { box-shadow: 0 0 0 10px rgba(61, 220, 151, 0); }
  100% { box-shadow: 0 0 0 0 rgba(61, 220, 151, 0); }
}

.hero-eq {
  margin-top: auto;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
  padding-top: 14px;
}

.eq {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.12);
  backdrop-filter: blur(8px);
  opacity: 0;
  transform: translateY(10px);
  animation: eq-in 0.7s cubic-bezier(.2, .8, .2, 1) forwards;
  transition: background 0.3s, transform 0.3s;

  &:hover { background: rgba(255, 255, 255, 0.14); transform: translateY(-2px); }
}

@keyframes eq-in { to { opacity: 1; transform: translateY(0); } }

.eq-icon {
  width: 34px;
  height: 34px;
  flex: 0 0 34px;
  border-radius: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.14);
  color: #c9f7b3;
}

.eq-text { min-width: 0; }

.eq-value {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-family: 'Sora', sans-serif;
  font-weight: 800;
  font-size: 1.05rem;
  font-variant-numeric: tabular-nums;
}

.eq-label {
  font-size: 0.72rem;
  line-height: 1.2;
  color: rgba(255, 255, 255, 0.72);
}

.hero-note {
  margin-top: 8px;
  font-size: 0.66rem;
  color: rgba(255, 255, 255, 0.5);
}
</style>
